import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Building2, Mail, MapPin, Phone } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { ReviewedBy } from "@/components/ReviewedBy";
import type { RangpurArea } from "@/content/rangpur-areas";
import { SITE } from "@/content/site";

/** Area page opening: H1, intro, alternate spellings, reviewer line, CTAs and contact card. */
export function AreaHero({ a }: { a: RangpurArea }) {
  return (
    <Section
      headingLevel={1}
      eyebrow={`Rangpur · ${a.name}`}
      title={
        <>
          SEO Expert near <span className="text-gradient">{a.name}.</span>
        </>
      }
    >
      <div className="grid lg:grid-cols-3 gap-10 -mt-4">
        <Reveal className="lg:col-span-2">
          <p className="text-xl text-muted-foreground leading-relaxed">{a.intro}</p>
          {a.aliases.length > 0 && (
            <p className="mt-4 text-sm text-muted-foreground">
              Also searched as:{" "}
              {a.aliases.map((x, i) => (
                <span key={x}>
                  {i > 0 ? ", " : ""}
                  <span className="text-foreground">{x}</span>
                </span>
              ))}
              .
            </p>
          )}
          <ReviewedBy
            credentials={`Local SEO consultant based in Rangpur · Planning for queries relevant to ${a.name}`}
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90"
            >
              Get a quote for {a.name} <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/seo-services/$location"
              params={{ location: "rangpur" }}
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card"
            >
              All Rangpur SEO services
            </Link>
            <Link
              to="/free-seo-audit"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card"
            >
              Free SEO audit
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-border p-8 bg-card space-y-4">
            <div className="flex items-center gap-3">
              <MapPin className="text-primary" size={18} />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Service area
                </div>
                <div className="font-display text-lg">{a.name}, Rangpur</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="text-primary" size={18} />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Coordinates
                </div>
                <div className="font-display text-lg">
                  {a.geo.lat}°N, {a.geo.lng}°E
                </div>
              </div>
            </div>
            <div className="border-t border-border pt-4 space-y-2 text-sm">
              <a
                href={`mailto:${SITE.email}`}
                className="flex items-center gap-3 hover:text-primary"
              >
                <Mail size={14} /> {SITE.email}
              </a>
              <a href={`tel:${SITE.phone}`} className="flex items-center gap-3 hover:text-primary">
                <Phone size={14} /> {SITE.phone}
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
